import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  StyleSheet,
  View,
  type ViewStyle,
} from "react-native";

import { ThemedText } from "@/components/themed-text";
import { Spacing } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";

export type LoadingProps = {
  /**
   * Replaces the default "Loading…". Pass `null` for a bare spinner — only
   * where surrounding copy already says what is happening, since the label is
   * what screen readers announce.
   */
  label?: string | null;
  size?: "small" | "large";
  /** Centre in the space available. Off for inline use inside a row. */
  fill?: boolean;
  /** Overrides the theme tint, for screens with their own palette. */
  color?: string;
  style?: ViewStyle;
};

/**
 * The app's one waiting state.
 *
 * Use this anywhere a screen or panel has nothing to show yet. Placeholder
 * blocks were tried and read as a broken layout rather than as progress — grey
 * bars do not move, so there is nothing to distinguish them from content that
 * failed to arrive.
 */
export function Loading({
  label,
  size = "large",
  fill = true,
  color,
  style,
}: LoadingProps) {
  const c = useTheme();
  const { t } = useTranslation();

  const text = label === null ? null : (label ?? t("common.loading"));

  return (
    <View
      style={[fill && styles.fill, styles.center, style]}
      accessible
      accessibilityRole="progressbar"
      // Announced even when the label is hidden: a silent spinner tells a
      // screen-reader user nothing at all.
      accessibilityLabel={text ?? t("common.loading")}
    >
      <ActivityIndicator size={size} color={color ?? c.tint} />

      {text ? (
        <ThemedText variant="caption" tone="muted" style={styles.label}>
          {text}
        </ThemedText>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  fill: { flex: 1 },
  center: { alignItems: "center", justifyContent: "center" },
  label: { marginTop: Spacing.md },
});
